import type { Context } from 'react';

type ContextRegistry = Map<string, Context<unknown>>;

type ContextRegistryHost = typeof globalThis & {
  __h2kReactContexts?: ContextRegistry;
};

/**
 * Keep a React Context identity stable for the life of the page.
 *
 * Vite can update a provider module and a consumer module in adjacent HMR
 * frames. A freshly-created Context object in either frame temporarily splits
 * an otherwise valid provider tree. The page registry makes both evaluations
 * resolve the same identity; a real navigation/reload still starts clean.
 */
export function pageLifetimeContext<T>(
  key: string,
  create: () => Context<T>,
): Context<T> {
  const host = globalThis as ContextRegistryHost;
  const registry = host.__h2kReactContexts ?? new Map<string, Context<unknown>>();
  host.__h2kReactContexts = registry;

  const existing = registry.get(key);
  if (existing) return existing as Context<T>;

  const context = create();
  registry.set(key, context as Context<unknown>);
  return context;
}
